// The land round the base for the 3D look: the north, south and east sides, past the map
// edge (the west is sea, see sea3d.ts). A war-worn approach: a cracked perimeter road with
// highways running in, checkpoints of jersey barriers, Hesco walls and sandbag nests where
// they reach the base, a line of razor wire, burnt-out cars and a dead tank, a container
// yard, ruins, craters and scorch marks, telephone poles, and fires that smoke. Past all
// that the land turns to forest, scorched at its ragged edge, out to the horizon, and the
// ground rises from the flat round the base into rolling, wooded hills.
// Scenery only: nothing out here is part of the game. Laid out once in tile space and
// turned with the view, so a view change costs nothing but a rotation.
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { MAP_SIZE, ROAD_WIDTH } from '@thrash/sim';
import { Fires } from './fire3d.ts';
import { photoGround } from './ground3d.ts';
import { FLOOR_PAD, HIGHWAY_ALONG0, roadStripMaterial } from './floor3d.ts';
import { farTrunk, pineCrown, pineTrunk, leafyCrown, leafyTrunk, snag, treeMaterials, treeWind } from './trees3d.ts';

const N = MAP_SIZE;
const C = N / 2;
/** How far out from the map edge the scenery runs, in tiles. */
const REACH = 46;
/** The perimeter road runs this far out from the map edge, round the three land sides. */
const RING = 5;
/** The razor wire runs this far out. */
const WIRE = 2.6;
const ROAD_W = 1.7;
/** Where the forest starts, in tiles out from the map edge (its edge wanders a few tiles either way), and how far it runs. */
const FOREST = REACH - 6;
const FOREST_FAR = 150;
/** Tiles covered by one repeat of the dirt texture. */
const GROUND_REPEAT = 16;
/** Roads running in: [axis, coordinate across, from, to]. 'x' runs east-west at y = across. */
type Road = { along: 'x' | 'y'; at: number; from: number; to: number; width: number };
const ROADS: Road[] = [
  // the perimeter, ending at the coast north and south
  { along: 'x', at: -RING, from: 0, to: N + RING, width: ROAD_W },
  { along: 'x', at: N + RING, from: 0, to: N + RING, width: ROAD_W },
  { along: 'y', at: N + RING, from: -RING, to: N + RING, width: ROAD_W },
  // highways in, from the east, the north and the south, each as far as the floor's edge: the floor draws them from there in (floor3d.ts)
  { along: 'x', at: 22, from: N + FLOOR_PAD, to: N + FOREST_FAR, width: ROAD_WIDTH },
  { along: 'y', at: 42, from: -FOREST_FAR, to: -FLOOR_PAD, width: ROAD_WIDTH },
  { along: 'y', at: 20, from: N + FLOOR_PAD, to: N + FOREST_FAR, width: ROAD_WIDTH },
];
/** Where each highway meets the base, and which way is out: a checkpoint goes there. */
const GATES: { x: number; y: number; ox: number; oy: number }[] = [
  { x: N, y: 22, ox: 1, oy: 0 },
  { x: 42, y: 0, ox: 0, oy: -1 },
  { x: 20, y: N, ox: 0, oy: 1 },
];

/** A seeded random stream, so the outskirts are the same every time. */
function stream(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Tiles out from the map edge on the land sides; 0 inside the map. */
const out = (x: number, y: number) => Math.max(-y, y - N, x - N, 0);

function onRoad(x: number, y: number, pad: number): boolean {
  for (const r of ROADS) {
    const [a, b] = r.along === 'x' ? [x, y] : [y, x];
    if (a >= r.from - pad && a <= r.to + pad && Math.abs(b - r.at) <= r.width / 2 + pad) return true;
  }
  return false;
}

/** The forest's edge at a point, in tiles out from the map edge: a ragged line that wanders round FOREST. */
const treeline = (x: number, y: number) =>
  FOREST + 3.5 * Math.sin(x * 0.19 + y * 0.11) + 2.5 * Math.sin(x * 0.07 - y * 0.23 + 1.3) + 1.5 * Math.sin((x + y) * 0.41 + 0.7);

const smoothstep = (a: number, b: number, v: number) => { const t = Math.min(1, Math.max(0, (v - a) / (b - a))); return t * t * (3 - 2 * t); };

/**
 * The ground's height at a tile point, in tiles: flat round the base (the game's own ground
 * and everything drawn on it stays at 0), swelling into rolling hills further out, and
 * easing back down to the coast and to the countryside at the far edge of the land.
 */
export function terrain(x: number, y: number): number {
  const d = out(x, y);
  if (d <= 6) return 0;
  const swell = 7 * smoothstep(7, 75, d);
  const hills = (Math.sin(x * 0.11 + 0.6) * Math.cos(y * 0.09 - 1.1) * 2.2
    + Math.sin(x * 0.043 - y * 0.051 + 2) * 2.8
    + Math.sin(x * 0.23 + y * 0.19) * 0.45) * smoothstep(7, 40, d);
  // hollows ease out a little below the base's level instead of sinking into pits
  const v = swell + hills;
  const soft = (v + Math.sqrt(v * v + 1)) / 2 - 0.5;
  return soft * smoothstep(6, 12, d) * smoothstep(0, 16, x) * (1 - smoothstep(140, 165, d));
}

/** The ground's upward normal at a tile point, in the scene's frame (x east, z south). */
function terrainNormal(x: number, y: number, n = new THREE.Vector3()): THREE.Vector3 {
  const e = 0.25;
  return n.set(terrain(x - e, y) - terrain(x + e, y), 2 * e, terrain(x, y - e) - terrain(x, y + e)).normalize();
}

/** Light from the north-west, for the shading painted onto the hills. */
const RELIEF_LIGHT = new THREE.Vector3(-0.5, 1, -0.4).normalize();

/**
 * How much brighter or darker the ground and the trees on it are painted at a tile point:
 * slopes facing the light lifted, slopes facing away darkened, flat ground left alone. Seen
 * from straight above, the real sun barely tells a hill from a plain; this does.
 */
function relief(x: number, y: number): number {
  const n = terrainNormal(x, y);
  return THREE.MathUtils.clamp(1 + 1.8 * (n.dot(RELIEF_LIGHT) - RELIEF_LIGHT.y), 0.55, 1.35);
}

/** A flat sheet over the given tile rectangle, cut into a grid and laid over the terrain. */
function terrainSheet(x0: number, x1: number, y0: number, y1: number, step: number, lift: number): THREE.BufferGeometry {
  const geo = new THREE.PlaneGeometry(x1 - x0, y1 - y0, Math.ceil((x1 - x0) / step), Math.ceil((y1 - y0) / step));
  geo.rotateX(-Math.PI / 2);
  geo.translate((x0 + x1) / 2 - C, 0, (y0 + y1) / 2 - C);
  const pos = geo.attributes.position!;
  const col = new Float32Array(pos.count * 4);
  for (let k = 0; k < pos.count; k++) {
    const x = pos.getX(k) + C, y = pos.getZ(k) + C;
    pos.setY(k, terrain(x, y) + lift);
    const f = relief(x, y);
    col.set([f, f, f, 1], k * 4);
  }
  geo.setAttribute('color', new THREE.BufferAttribute(col, 4));
  geo.computeVertexNormals();
  return geo;
}

/** Near a checkpoint, where the scenery is laid by hand. */
const nearGate = (x: number, y: number, r: number) => GATES.some(g => Math.hypot(x - g.x, y - g.y) < r);

// ---- textures ------------------------------------------------------------------------

function canvas(n: number, draw: (g: CanvasRenderingContext2D, n: number) => void, wrap = true): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = c.height = n;
  draw(c.getContext('2d')!, n);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  if (wrap) t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.anisotropy = 4;
  return t;
}

function blot(kind: 'crater' | 'scorch' | 'mud'): THREE.CanvasTexture {
  return canvas(128, (g, n) => {
    const r = stream(kind.length * 977);
    const h = n / 2;
    if (kind === 'crater') {
      const lip = g.createRadialGradient(h, h, h * 0.35, h, h, h);
      lip.addColorStop(0, 'rgba(70,58,40,0.95)');
      lip.addColorStop(0.55, 'rgba(90,76,52,0.8)');
      lip.addColorStop(1, 'rgba(90,76,52,0)');
      g.fillStyle = lip;
      g.fillRect(0, 0, n, n);
      const pit = g.createRadialGradient(h, h, 0, h, h, h * 0.5);
      pit.addColorStop(0, 'rgba(12,10,8,1)');
      pit.addColorStop(0.7, 'rgba(30,24,18,0.95)');
      pit.addColorStop(1, 'rgba(40,32,22,0)');
      g.fillStyle = pit;
      g.fillRect(0, 0, n, n);
      // thrown clods
      for (let i = 0; i < 40; i++) {
        const a = r() * Math.PI * 2, d = h * (0.45 + r() * 0.5);
        g.fillStyle = `rgba(${40 + r() * 30},${34 + r() * 20},${24},${0.6 + r() * 0.4})`;
        g.beginPath();
        g.arc(h + Math.cos(a) * d, h + Math.sin(a) * d, 1 + r() * 3, 0, Math.PI * 2);
        g.fill();
      }
    } else {
      const [c0, c1] = kind === 'scorch' ? ['rgba(8,8,6,0.9)', 'rgba(20,16,10,0)'] : ['rgba(48,38,24,0.85)', 'rgba(60,48,30,0)'];
      for (let i = 0; i < 9; i++) {
        const x = h + (r() - 0.5) * h * 0.7, y = h + (r() - 0.5) * h * 0.7, rad = h * (0.3 + r() * 0.4);
        const grad = g.createRadialGradient(x, y, 0, x, y, rad);
        grad.addColorStop(0, c0);
        grad.addColorStop(1, c1);
        g.fillStyle = grad;
        g.fillRect(0, 0, n, n);
      }
    }
  }, false);
}

/**
 * War-worn ground for the land sides: churned mud and dry earth, dead grass in tufts,
 * gravel, and tyre tracks gouged through it. One repeat covers GROUND_REPEAT tiles.
 */
function dirt(): THREE.CanvasTexture {
  return canvas(512, (g, n) => {
    const r = stream(404);
    g.fillStyle = '#4a4532';
    g.fillRect(0, 0, n, n);
    // big soft patches of mud, dry earth and dead grass, drawn wrapped so the tile repeats cleanly
    const blob = (x: number, y: number, rad: number, color: string) => {
      for (const dx of [-n, 0, n]) for (const dy of [-n, 0, n]) {
        const grad = g.createRadialGradient(x + dx, y + dy, 0, x + dx, y + dy, rad);
        grad.addColorStop(0, color);
        grad.addColorStop(1, color.replace(/[\d.]+\)$/, '0)'));
        g.fillStyle = grad;
        g.fillRect(x + dx - rad, y + dy - rad, rad * 2, rad * 2);
      }
    };
    const tones = ['rgba(58,48,32,0.7)', 'rgba(92,80,54,0.6)', 'rgba(64,68,40,0.55)', 'rgba(40,34,24,0.6)', 'rgba(110,96,70,0.45)'];
    for (let i = 0; i < 90; i++) blob(r() * n, r() * n, 20 + r() * 70, tones[Math.floor(r() * tones.length)]!);
    // tufts of dead grass
    for (let i = 0; i < 1600; i++) {
      const x = r() * n, y = r() * n, l = 2 + r() * 5;
      g.strokeStyle = `rgba(${120 + r() * 50},${110 + r() * 40},${60 + r() * 30},${0.25 + r() * 0.35})`;
      g.lineWidth = 1;
      g.beginPath(); g.moveTo(x, y); g.lineTo(x + (r() - 0.5) * 3, y - l); g.stroke();
    }
    // gravel and grit
    for (let i = 0; i < 9000; i++) {
      const v = r();
      g.fillStyle = v < 0.5 ? `rgba(20,18,14,${0.3 + r() * 0.4})` : `rgba(${140 + r() * 60},${130 + r() * 50},${110 + r() * 40},${0.2 + r() * 0.35})`;
      g.fillRect(r() * n, r() * n, 1 + r() * 1.5, 1 + r() * 1.5);
    }
    // tyre tracks: pairs of dark ruts curving across, wrapped
    for (let i = 0; i < 4; i++) {
      let x = r() * n, y = r() * n, a = r() * Math.PI * 2;
      const pts: [number, number][] = [];
      for (let k = 0; k < 60; k++) { pts.push([x, y]); a += (r() - 0.5) * 0.15; x += Math.cos(a) * 9; y += Math.sin(a) * 9; }
      for (const side of [-5, 5]) {
        g.strokeStyle = 'rgba(28,24,18,0.28)';
        g.lineWidth = 3;
        for (const dx of [-n, 0, n]) for (const dy of [-n, 0, n]) {
          g.beginPath();
          pts.forEach(([px, py], k) => {
            const nx = px + dx - Math.sin(a) * side, ny = py + dy + Math.cos(a) * side;
            if (k) g.lineTo(nx, ny); else g.moveTo(nx, ny);
          });
          g.stroke();
        }
      }
    }
  });
}

/** Hesco: sandy fabric showing through a heavy wire mesh. */
function hescoTex(): THREE.CanvasTexture {
  return canvas(64, (g, n) => {
    const r = stream(5);
    g.fillStyle = '#9a8a62';
    g.fillRect(0, 0, n, n);
    for (let i = 0; i < 300; i++) {
      g.fillStyle = `rgba(${110 + r() * 50},${96 + r() * 40},${60 + r() * 30},0.5)`;
      g.fillRect(r() * n, r() * n, 2, 2);
    }
    g.strokeStyle = 'rgba(50,48,40,0.9)';
    g.lineWidth = 2;
    for (let k = 0; k <= n; k += n / 4) {
      g.beginPath(); g.moveTo(k, 0); g.lineTo(k, n); g.stroke();
      g.beginPath(); g.moveTo(0, k); g.lineTo(n, k); g.stroke();
    }
  });
}

/** Corrugated, rust-streaked steel for shipping containers; tinted per container. */
function containerTex(): THREE.CanvasTexture {
  return canvas(128, (g, n) => {
    const r = stream(21);
    g.fillStyle = '#d8d8d8';
    g.fillRect(0, 0, n, n);
    for (let x = 0; x < n; x += 6) {
      g.fillStyle = 'rgba(0,0,0,0.18)';
      g.fillRect(x, 0, 2, n);
    }
    for (let i = 0; i < 26; i++) {
      const x = r() * n;
      const grad = g.createLinearGradient(0, 0, 0, n);
      grad.addColorStop(0, 'rgba(110,60,30,0.6)');
      grad.addColorStop(1, 'rgba(110,60,30,0)');
      g.fillStyle = grad;
      g.fillRect(x, 0, 2 + r() * 5, n * (0.3 + r() * 0.7));
    }
  });
}

/** Moss, needles and dark earth under the trees. */
function forestFloor(): THREE.CanvasTexture {
  const r = stream(91);
  return canvas(256, (g, n) => {
    g.fillStyle = '#2c3a22';
    g.fillRect(0, 0, n, n);
    const palette = ['#24321d', '#34452a', '#3b3a26', '#2a2f1e', '#435532', '#4a4029'];
    for (let i = 0; i < 420; i++) {
      g.fillStyle = palette[Math.floor(r() * palette.length)]!;
      const x = r() * n, y = r() * n, rad = 2 + r() * 12;
      g.beginPath();
      g.ellipse(x, y, rad, rad * (0.4 + r() * 0.6), r() * Math.PI, 0, Math.PI * 2);
      g.fill();
    }
  });
}

// ---- shapes --------------------------------------------------------------------------

/** A geometry with its base on the ground at y = 0. */
const seated = (geo: THREE.BufferGeometry): THREE.BufferGeometry => {
  geo.computeBoundingBox();
  geo.translate(0, -geo.boundingBox!.min.y, 0);
  return geo;
};

/** A jersey barrier: a sloped concrete wall one and a bit tiles long along x. */
function jerseyGeo(): THREE.BufferGeometry {
  const s = new THREE.Shape();
  s.moveTo(-0.24, 0); s.lineTo(0.24, 0); s.lineTo(0.16, 0.1); s.lineTo(0.07, 0.42); s.lineTo(-0.07, 0.42); s.lineTo(-0.16, 0.1); s.closePath();
  const geo = new THREE.ExtrudeGeometry(s, { depth: 1.1, bevelEnabled: false });
  geo.translate(0, 0, -0.55);
  geo.rotateY(Math.PI / 2);
  return seated(geo);
}

/** A Czech hedgehog: three steel beams crossed. */
function hedgehogGeo(): THREE.BufferGeometry {
  const beams: THREE.BufferGeometry[] = [];
  const dirs = [new THREE.Euler(0.62, 0, 0.62), new THREE.Euler(-0.62, 0, 0.62), new THREE.Euler(0, 0, -0.95)];
  for (const e of dirs) {
    const b = new THREE.BoxGeometry(1.0, 0.07, 0.07);
    b.applyMatrix4(new THREE.Matrix4().makeRotationFromEuler(e));
    beams.push(b);
  }
  return seated(mergeGeometries(beams)!);
}

/** Concertina wire: a coil one segment long along x. */
function wireGeo(len: number): THREE.BufferGeometry {
  const loops = 9, rad = 0.2;
  const pts: THREE.Vector3[] = [];
  for (let i = 0; i <= loops * 16; i++) {
    const t = i / (loops * 16), a = t * loops * Math.PI * 2;
    pts.push(new THREE.Vector3(-len / 2 + t * len + Math.sin(a) * 0.04, rad + Math.sin(a) * rad, Math.cos(a) * rad));
  }
  return new THREE.TubeGeometry(new THREE.CatmullRomCurve3(pts), loops * 16, 0.008, 3, false);
}

/** A burnt-out car: body and cabin, no wheels left. */
function carGeo(): THREE.BufferGeometry {
  const body = new THREE.BoxGeometry(1.5, 0.32, 0.68); body.translate(0, 0.22, 0);
  const cabin = new THREE.BoxGeometry(0.78, 0.26, 0.6); cabin.translate(-0.08, 0.5, 0);
  const sag = new THREE.BoxGeometry(1.3, 0.08, 0.6); sag.translate(0.05, 0.04, 0);
  return mergeGeometries([body, cabin, sag])!;
}

/** A broken wall: a slab with a jagged top, for ruins. */
function wallGeo(): THREE.BufferGeometry {
  const s = new THREE.Shape();
  s.moveTo(-0.5, 0); s.lineTo(0.5, 0); s.lineTo(0.5, 0.55); s.lineTo(0.32, 0.8); s.lineTo(0.18, 0.62); s.lineTo(0.02, 1.05);
  s.lineTo(-0.2, 0.9); s.lineTo(-0.33, 1.1); s.lineTo(-0.5, 0.95); s.closePath();
  const geo = new THREE.ExtrudeGeometry(s, { depth: 0.14, bevelEnabled: false });
  geo.translate(0, 0, -0.07);
  return geo;
}

// ---- the layout ----------------------------------------------------------------------

/** One instance: tile position, turn, scale, tint. */
interface Put { x: number; y: number; h?: number; rot?: number; tilt?: [number, number]; s?: [number, number, number]; color?: number }

/** A kind of prop drawn as one instanced mesh. */
class Batch {
  readonly puts: Put[] = [];
  /** `drape` tilts each instance to lie along the slope it sits on (ground marks); `shade` paints it with the hill shading (trees). */
  constructor(readonly geo: THREE.BufferGeometry, readonly mat: THREE.Material, readonly shadow = true, readonly drape = false, readonly shade = false) {}
  add(p: Put): void { this.puts.push(p); }

  build(): THREE.InstancedMesh {
    const m = new THREE.InstancedMesh(this.geo, this.mat, Math.max(1, this.puts.length));
    m.count = this.puts.length;
    const mat = new THREE.Matrix4(), q = new THREE.Quaternion(), e = new THREE.Euler(), v = new THREE.Vector3(), sc = new THREE.Vector3();
    const col = new THREE.Color();
    const up = new THREE.Vector3(0, 1, 0), n = new THREE.Vector3(), slope = new THREE.Quaternion();
    this.puts.forEach((p, i) => {
      e.set(p.tilt?.[0] ?? 0, p.rot ?? 0, p.tilt?.[1] ?? 0, 'YXZ');
      q.setFromEuler(e);
      if (this.drape) q.premultiply(slope.setFromUnitVectors(up, terrainNormal(p.x, p.y, n)));
      // everything stands on the terrain
      v.set(p.x - C, (p.h ?? 0) + terrain(p.x, p.y), p.y - C);
      sc.set(...(p.s ?? [1, 1, 1]));
      m.setMatrixAt(i, mat.compose(v, q, sc));
      if (p.color !== undefined) m.setColorAt(i, col.set(p.color).multiplyScalar(this.shade ? relief(p.x, p.y) : 1));
    });
    m.castShadow = this.shadow;
    m.receiveShadow = true;
    m.frustumCulled = false;
    return m;
  }
}

const std = (color: number, roughness = 0.9, metalness = 0, map?: THREE.Texture) =>
  new THREE.MeshStandardMaterial({ color, roughness, metalness, map: map ?? null });


export class Outskirts3D {
  readonly group = new THREE.Group();
  private readonly fires = new Fires();

  constructor() {
    this.group.name = 'outskirts';
    this.lay();
  }

  /** Turns the outskirts with the view, as scene3d's ground() turns a tile point. */
  setAngle(angle: number): void {
    this.group.rotation.y = -angle;
  }

  /** Flickers the flames and moves the smoke on. `now` is milliseconds. */
  update(now: number): void {
    treeWind.value = now / 1000;
    this.fires.update(now);
  }

  private lay(): void {
    const r = stream(1337);
    const g = this.group;

    // ---- the dirt: one sheet over the land sides, under the map's own ground and the sea ----
    {
      const W = N + 200, x0 = 0.9, y0 = N / 2 - (N + 400) / 2;
      const geo = terrainSheet(x0, W, y0, y0 + N + 400, 2, 0);
      const uv = geo.attributes.uv!, pos = geo.attributes.position!;
      for (let k = 0; k < uv.count; k++) uv.setXY(k, (pos.getX(k) + C) / GROUND_REPEAT, (pos.getZ(k) + C) / GROUND_REPEAT);
      const mat = new THREE.MeshLambertMaterial({ map: dirt(), vertexColors: true });
      // photographed churned earth, one repeat to fourteen tiles, over the painted mud spread four times wider;
      // the floor (floor3d.ts) runs out over it as far as FLOOR_PAD and gives out into it there
      photoGround(mat, { name: 'dirt_aerial_02', scale: GROUND_REPEAT / 14, macro: 0.4, mix: 0.9, bump: 1.3, tint: 0.6, gain: 0.7 });
      const m = new THREE.Mesh(geo, mat);
      m.position.y = -0.01;
      m.receiveShadow = true;
      g.add(m);
    }

    // ---- roads: the floor's asphalt (floor3d.ts), as strips that follow the ground ----
    const roadMat = roadStripMaterial();
    ROADS.forEach((rd, i) => {
      const len = rd.to - rd.from;
      // cut into one-tile lengths so the road can follow the ground over the hills
      const geo = new THREE.PlaneGeometry(len, rd.width, Math.ceil(len), 1);
      geo.rotateX(-Math.PI / 2);
      const mid = (rd.from + rd.to) / 2;
      if (rd.along === 'x') geo.translate(mid - C, 0, rd.at - C);
      else { geo.rotateY(-Math.PI / 2); geo.translate(rd.at - C, 0, mid - C); }
      // later roads sit a hair higher, so crossings don't flicker
      const pos = geo.attributes.position!, uv = geo.attributes.uv!;
      const road = new Float32Array(pos.count * 3);
      for (let k = 0; k < pos.count; k++) {
        const x = pos.getX(k) + C, y = pos.getZ(k) + C;
        pos.setY(k, terrain(x, y) + 0.004 + i * 0.0015);
        // the noise in tile space, and where along and across the road this is: a highway counts its tiles out from the floor's edge
        uv.setXY(k, x, y);
        const [a, b] = rd.along === 'x' ? [x, y] : [y, x];
        const along = rd.from < 0 ? -FLOOR_PAD - a + HIGHWAY_ALONG0 : a - rd.from + HIGHWAY_ALONG0;
        road.set([along, b - rd.at, rd.width / 2], k * 3);
      }
      geo.setAttribute('aRoad', new THREE.BufferAttribute(road, 3));
      geo.computeVertexNormals();
      const m = new THREE.Mesh(geo, roadMat);
      m.receiveShadow = true;
      g.add(m);
    });

    // ---- ground marks ----
    const decal = (tex: THREE.Texture, opacity = 1) => {
      const geo = new THREE.PlaneGeometry(1, 1);
      geo.rotateX(-Math.PI / 2);
      return new Batch(geo, new THREE.MeshLambertMaterial({ map: tex, transparent: true, opacity, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2 }), false, true);
    };
    const craters = decal(blot('crater'));
    const scorch = decal(blot('scorch'));
    const mud = decal(blot('mud'), 0.85);

    // ---- props ----
    const concrete = std(0x8d8a80, 0.95);
    const jersey = new Batch(jerseyGeo(), concrete);
    const hesco = new Batch(seated(new THREE.BoxGeometry(0.85, 0.85, 0.85)), std(0xffffff, 1, 0, hescoTex()));
    const sandbag = new Batch(seated(new THREE.CapsuleGeometry(0.085, 0.2, 3, 8).rotateZ(Math.PI / 2).scale(1, 0.62, 1)), std(0x8a7a55, 1));
    const hedgehog = new Batch(hedgehogGeo(), std(0x3a3532, 0.6, 0.6));
    const wire = new Batch(wireGeo(1.2), std(0x6a6a66, 0.4, 0.9), true);
    const post = new Batch(seated(new THREE.CylinderGeometry(0.025, 0.03, 0.75, 5)), std(0x4a3a2a, 1));
    const car = new Batch(carGeo(), std(0xffffff, 0.85, 0.3));
    const container = new Batch(seated(new THREE.BoxGeometry(2.4, 1.0, 1.0)), std(0xffffff, 0.75, 0.35, containerTex()));
    const drum = new Batch(seated(new THREE.CylinderGeometry(0.13, 0.13, 0.36, 10)), std(0xffffff, 0.6, 0.5));
    const tire = new Batch(seated(new THREE.TorusGeometry(0.14, 0.06, 6, 12).rotateX(Math.PI / 2)), std(0x1a1a1a, 0.95));
    const chunk = new Batch(seated(new THREE.DodecahedronGeometry(0.12, 0)), std(0x7d7a72, 1));
    const wall = new Batch(wallGeo(), std(0x9a8f7e, 0.95));
    const pole = new Batch(seated(new THREE.CylinderGeometry(0.035, 0.05, 2.3, 6)), std(0x4a3a28, 1));
    const crossarm = new Batch(new THREE.BoxGeometry(0.06, 0.06, 0.7), std(0x4a3a28, 1));

    const fireAt: [number, number, number, number][] = [];

    // a row of sandbags `count` long from (x, y) towards (dx, dy), `layers` high, bricked
    const bags = (x: number, y: number, dx: number, dy: number, count: number, layers: number) => {
      const rot = Math.atan2(-dy, dx);
      for (let l = 0; l < layers; l++) {
        for (let i = 0; i < count - (l & 1); i++) {
          const t = i * 0.3 + (l & 1) * 0.15;
          sandbag.add({ x: x + dx * t + (r() - 0.5) * 0.03, y: y + dy * t + (r() - 0.5) * 0.03, h: l * 0.1, rot: rot + (r() - 0.5) * 0.15 });
        }
      }
    };
    /** A U of sandbags open towards the base, a fighting position. */
    const nest = (x: number, y: number, ox: number, oy: number) => {
      const px = -oy, py = ox;
      bags(x + ox * 0.6 - px * 0.6, y + oy * 0.6 - py * 0.6, px, py, 5, 3);
      bags(x - px * 0.7 + ox * 0.6, y - py * 0.7 + oy * 0.6, -ox, -oy, 4, 3);
      bags(x + px * 0.7 + ox * 0.6, y + py * 0.7 + oy * 0.6, -ox, -oy, 4, 3);
    };

    // ---- checkpoints where the highways reach the base ----
    for (const gt of GATES) {
      const px = -gt.oy, py = gt.ox; // across the road
      const at = (o: number, a: number) => [gt.x + gt.ox * o + px * a, gt.y + gt.oy * o + py * a] as const;
      const rotAcross = Math.atan2(-py, px);
      // a chicane of jersey barriers out on the approach: alternate sides, so traffic has to weave
      for (let k = 0; k < 4; k++) {
        const side = k & 1 ? 1 : -1;
        const [x, y] = at(8 + k * 1.6, side * 0.45);
        jersey.add({ x, y, rot: rotAcross + (r() - 0.5) * 0.12 });
      }
      // Hesco walls either side of the road at the wire line, leaving the road a gap
      for (const side of [-1, 1]) {
        for (let k = 0; k < 4; k++) {
          const [x, y] = at(WIRE, side * (1.6 + k * 0.85));
          hesco.add({ x, y, rot: rotAcross });
          if (k < 2) { const [x2, y2] = at(WIRE + 0.85, side * (1.6 + k * 0.85)); hesco.add({ x: x2, y: y2, rot: rotAcross }); }
        }
        const [nx, ny] = at(1.3, side * 2.7); // inside the wire, covering the gap
        nest(nx, ny, -gt.ox, -gt.oy);
        // a burning barrel by each nest
        const [bx, by] = at(WIRE + 1.2, side * 1.25);
        drum.add({ x: bx, y: by, color: 0x5a2a1a });
        fireAt.push([bx, by, 0.36, 0.45]);
      }
      // a barrier half across the road at the gate, and one knocked askew
      { const [x, y] = at(WIRE + 0.2, -0.5); jersey.add({ x, y, rot: rotAcross }); }
      { const [x, y] = at(WIRE + 1.4, 0.7); jersey.add({ x, y, rot: rotAcross + 0.5 }); }
      // drums and tyres stacked by the wall
      for (let k = 0; k < 5; k++) {
        const [x, y] = at(0.6 + r() * 0.9, (k & 1 ? 1 : -1) * (4.2 + r() * 0.8));
        drum.add({ x, y, color: [0x3d4a2a, 0x6a3420, 0x2b3a4a][k % 3] });
      }
      for (let k = 0; k < 3; k++) {
        const [x, y] = at(1.2, -5.4 + k * 0.03);
        tire.add({ x, y, h: k * 0.12 });
      }
    }

    // ---- razor wire, round the three land sides, with gaps for the roads ----
    const SEG = 1.2;
    const wireRun = (fx: (t: number) => [number, number], from: number, to: number, rot: number) => {
      for (let t = from; t < to; t += SEG) {
        const [x, y] = fx(t + SEG / 2);
        if (onRoad(x, y, 0.4) || nearGate(x, y, 4.6)) continue;
        wire.add({ x, y, rot: rot + (r() - 0.5) * 0.06 });
        if (r() < 0.5) post.add({ x: fx(t)[0], y: fx(t)[1], tilt: [(r() - 0.5) * 0.2, (r() - 0.5) * 0.2] });
      }
    };
    wireRun(t => [t, -WIRE], 0.4, N + WIRE, 0);
    wireRun(t => [t, N + WIRE], 0.4, N + WIRE, 0);
    wireRun(t => [N + WIRE, t], -WIRE, N + WIRE, Math.PI / 2);

    // ---- hedgehogs: in the shallows all down the coast, and strewn in the fields ----
    for (let y = -REACH; y < N + REACH; y += 1.4 + r() * 1.6) {
      hedgehog.add({ x: -1.4 - r() * 3.5, y, rot: r() * 6.28, h: -0.08 - r() * 0.1, s: [1.1, 1.1, 1.1] });
    }

    // ---- wrecks on the roads ----
    const wreck = (x: number, y: number, rot: number, burning: boolean, flipped = false) => {
      car.add({ x, y, rot, h: flipped ? 0.72 : 0, tilt: flipped ? [Math.PI, 0] : [(r() - 0.5) * 0.08, (r() - 0.5) * 0.12], color: [0x2a2622, 0x3a2a20, 0x4a3a2c, 0x24282a][Math.floor(r() * 4)] });
      scorch.add({ x, y, rot: r() * 6, s: [2.6, 1, 2.6] });
      for (let k = 0; k < 5; k++) chunk.add({ x: x + (r() - 0.5) * 2, y: y + (r() - 0.5) * 2, rot: r() * 6, s: [0.5, 0.4, 0.5], color: 0x2a2a2a });
      if (burning) fireAt.push([x, y, 0.45, 1.3]);
    };
    wreck(N + 14, 22.6, 0.25, true);
    wreck(N + 23, 21.3, -2.7, false, true);
    wreck(N + 34, 22.9, 1.6, false);
    wreck(42.5, -15, 1.3, true);
    wreck(41.2, -27, 0.4, false);
    wreck(19.4, N + 17, -1.2, false, true);
    wreck(20.8, N + 31, 1.9, true);
    wreck(N + RING + 0.3, 40, 1.4, false);
    wreck(10, -RING - 0.4, 0.1, false);

    // ---- a container yard off the east highway ----
    const yard = [[N + 12, 27.5], [N + 12, 28.6], [N + 12, 29.7], [N + 15, 27.8], [N + 15, 29.2]] as const;
    yard.forEach(([x, y], i) => {
      const color = [0x8a3a26, 0x3a5a7a, 0x5a6a3a, 0x9a6a2a, 0x6a6a6a][i % 5]!;
      container.add({ x, y, rot: (r() - 0.5) * 0.08, color });
      if (i < 2) container.add({ x: x + 0.2, y, h: 1.0, rot: (r() - 0.5) * 0.15, color: [0x5a2a1a, 0x2a3a4a][i]! });
    });
    container.add({ x: N + 9, y: 31, rot: 1.1, tilt: [0, 0.35], color: 0x7a3a22 }); // one dumped on its side
    for (let k = 0; k < 12; k++) drum.add({ x: N + 13.5 + r() * 3, y: 25.6 + r() * 1.2, color: [0x3d4a2a, 0x6a3420, 0x2b3a4a, 0x8a7a2a][k % 4], tilt: k % 5 === 0 ? [Math.PI / 2, 0] : undefined, h: k % 5 === 0 ? 0.13 : 0 });

    // ---- telephone poles down the highways, some leaning, wires sagging between ----
    const wires: number[] = [];
    const tops: THREE.Vector3[][] = [];
    const poleRun = (pts: [number, number][], rot: number) => {
      const run: THREE.Vector3[] = [];
      for (const [x, y] of pts) {
        const lean = r() < 0.2 ? (r() - 0.5) * 0.35 : 0;
        pole.add({ x, y, rot, tilt: [lean, 0] });
        crossarm.add({ x, y, h: 2.1, rot });
        run.push(new THREE.Vector3(x - C, 2.1 + terrain(x, y), y - C));
      }
      tops.push(run);
    };
    poleRun(Array.from({ length: 10 }, (_, i) => [N + 8 + i * 4.2, 22 - 1.6] as [number, number]), 0);
    poleRun(Array.from({ length: 10 }, (_, i) => [42 + 1.4, -8 - i * 4.2] as [number, number]), Math.PI / 2);
    poleRun(Array.from({ length: 10 }, (_, i) => [20 - 1.4, N + 8 + i * 4.2] as [number, number]), Math.PI / 2);
    for (const run of tops) {
      for (let i = 0; i + 1 < run.length; i++) {
        if (r() < 0.12) continue; // a cut line
        for (const off of [-0.28, 0.28]) {
          const a = run[i]!, b = run[i + 1]!;
          const along = b.clone().sub(a).normalize();
          const side = new THREE.Vector3(-along.z, 0, along.x).multiplyScalar(off);
          let prev = a.clone().add(side);
          for (let k = 1; k <= 8; k++) {
            const t = k / 8;
            const p = a.clone().lerp(b, t).add(side);
            p.y -= Math.sin(t * Math.PI) * 0.35;
            wires.push(prev.x, prev.y, prev.z, p.x, p.y, p.z);
            prev = p;
          }
        }
      }
    }
    const lineGeo = new THREE.BufferGeometry();
    lineGeo.setAttribute('position', new THREE.Float32BufferAttribute(wires, 3));
    g.add(new THREE.LineSegments(lineGeo, new THREE.LineBasicMaterial({ color: 0x141414 })));

    // ---- ruins: broken walls round a heap of rubble ----
    const ruins: [number, number][] = [];
    for (let tries = 0; ruins.length < 14 && tries < 400; tries++) {
      const x = r() * (N + 36), y = -36 + r() * (N + 72);
      const d = out(x, y);
      if (d < 8 || d > 38 || x < 3 || onRoad(x, y, 3) || nearGate(x, y, 10)) continue;
      if (ruins.some(([a, b]) => Math.hypot(a - x, b - y) < 7)) continue;
      ruins.push([x, y]);
      const rot = r() * Math.PI, c = Math.cos(rot), s = Math.sin(rot);
      const size = 1.6 + r() * 1.2;
      // two or three walls of a square house, standing on its edges
      const sides: [number, number, number][] = [[0, -1, 0], [1, 0, Math.PI / 2], [0, 1, 0], [-1, 0, Math.PI / 2]];
      sides.forEach(([ex, ey, wr]) => {
        if (r() < 0.35) return;
        const lx = ex * size / 2, ly = ey * size / 2;
        wall.add({ x: x + lx * c - ly * s, y: y + lx * s + ly * c, rot: -rot - wr, s: [size, 0.6 + r() * 0.8, 1], color: [0x9a8f7e, 0x8a7a6a, 0xa09888][Math.floor(r() * 3)] });
      });
      this.loadModel(r() < 0.5 ? 'rubble_medium' : 'rubble_small', x, y, r() * 6, 0.9);
      for (let k = 0; k < 14; k++) chunk.add({ x: x + (r() - 0.5) * size * 2, y: y + (r() - 0.5) * size * 2, rot: r() * 6, s: [0.6 + r(), 0.5 + r() * 0.6, 0.6 + r()] });
      scorch.add({ x, y, rot: r() * 6, s: [size * 1.6, 1, size * 1.6] });
      if (r() < 0.3) fireAt.push([x, y, 0.15, 0.7]);
    }

    // ---- a dead tank out by the north road, and one on the east field ----
    this.loadModel('tank', 46, -11, 2.2, 1.35, true);
    scorch.add({ x: 46, y: -11, s: [3.4, 1, 3.4] });
    fireAt.push([46, -11, 0.6, 1.4]);
    this.loadModel('tank', N + 26, 33, -0.6, 1.35, true);
    scorch.add({ x: N + 26, y: 33, s: [3, 1, 3] });

    // ---- the ground itself: craters, mud and churned earth, debris, scattered hedgehogs and drums ----
    const scatter = (count: number, minOut: number, maxOut: number, roadPad: number, fn: (x: number, y: number) => void) => {
      for (let i = 0, tries = 0; i < count && tries < count * 20; tries++) {
        const x = r() * (N + maxOut), y = -maxOut + r() * (N + 2 * maxOut);
        const d = out(x, y);
        if (d < minOut || d > maxOut || x < 0.8 || onRoad(x, y, roadPad) || nearGate(x, y, 5)) continue;
        // denser close to the base, where the fighting was
        if (r() > 1.15 - d / maxOut) continue;
        fn(x, y);
        i++;
      }
    };
    scatter(70, 3, REACH, 0.4, (x, y) => { const s = 0.9 + r() * 2.2; craters.add({ x, y, rot: r() * 6, s: [s, 1, s] }); });
    scatter(55, 1, REACH, -0.2, (x, y) => { const s = 1.5 + r() * 3.5; mud.add({ x, y, rot: r() * 6, s: [s, 1, s * (0.6 + r() * 0.4)] }); });
    scatter(26, 2, REACH, 0, (x, y) => { const s = 1.2 + r() * 2; scorch.add({ x, y, rot: r() * 6, s: [s, 1, s] }); });
    scatter(380, 1.5, REACH, 0.2, (x, y) => chunk.add({ x, y, rot: r() * 6, s: [0.5 + r(), 0.4 + r() * 0.5, 0.5 + r()], color: r() < 0.3 ? 0x5a5248 : 0x7d7a72 }));
    scatter(40, 7, 30, 0.8, (x, y) => hedgehog.add({ x, y, rot: r() * 6.28, tilt: r() < 0.2 ? [0.5, 0] : undefined }));
    scatter(30, 4, 30, 0.6, (x, y) => drum.add({ x, y, color: [0x3d4a2a, 0x6a3420, 0x2b3a4a][Math.floor(r() * 3)], tilt: r() < 0.4 ? [Math.PI / 2, 0] : undefined, h: 0 }));
    scatter(14, 6, 34, 1, (x, y) => { // abandoned barricades: a pair of jersey barriers, one toppled
      const rot = r() * 6;
      jersey.add({ x, y, rot });
      jersey.add({ x: x + Math.cos(rot) * 1.2, y: y - Math.sin(rot) * 1.2, rot: rot + 0.3, tilt: [0, r() < 0.5 ? 1.4 : 0], h: 0 });
    });
    // potholes on the roads themselves
    for (const rd of ROADS) {
      for (let a = rd.from + 2; a < rd.to; a += 3 + r() * 7) {
        const b = rd.at + (r() - 0.5) * rd.width * 0.7, s = 0.35 + r() * 0.5;
        const [x, y] = rd.along === 'x' ? [a, b] : [b, a];
        if (x < 0.5) continue;
        craters.add({ x, y, rot: r() * 6, s: [s, 1, s] });
      }
    }

    for (const b of [mud, craters, scorch, jersey, hesco, sandbag, hedgehog, wire, post, car, container, drum, tire, chunk, wall, pole, crossarm]) {
      if (b.puts.length) g.add(b.build());
    }
    fireAt.forEach(([x, y, h, size], i) => this.fires.add(x - C, h + terrain(x, y), y - C, size, i + 1));
    this.fires.build();
    g.add(this.fires.group);
    this.plantForest();
  }

  /** The forest round the land sides: a ragged, scorched edge, then dense woods out to the horizon. */
  private plantForest(): void {
    const r = stream(4242);
    const g = this.group;

    // the forest floor fades in over the dirt along the treeline
    {
      const x0 = 0.9, x1 = N + 200, y0 = N / 2 - (N + 400) / 2, y1 = N / 2 + (N + 400) / 2;
      const geo = terrainSheet(x0, x1, y0, y1, 2, 0);
      const pos = geo.attributes.position!, uv = geo.attributes.uv!;
      const col = geo.attributes.color!;
      for (let k = 0; k < pos.count; k++) {
        const x = pos.getX(k) + C, y = pos.getZ(k) + C;
        uv.setXY(k, x / 10, y / 10);
        col.setW(k, smoothstep(-3, 2, out(x, y) - treeline(x, y)));
      }
      const mat = new THREE.MeshLambertMaterial({ map: forestFloor(), vertexColors: true, transparent: true, depthWrite: false, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 });
      // photographed moss and leaf litter, one repeat to four tiles
      photoGround(mat, { name: 'forest_leaves_02', scale: 10 / 4, macro: 0.5, mix: 0.85, tint: 0.7, gain: 0.55 });
      const m = new THREE.Mesh(geo, mat);
      m.position.y = -0.005;
      m.receiveShadow = true;
      g.add(m);
    }

    // the forest is cut into square blocks, each its own set of instanced meshes, so the blocks
    // off screen are culled; blocks near the base cast shadows, the far ones (only ever seen
    // zoomed right out) don't, and lose their trunks under the canopy
    const BLOCK = 40, NEAR = FOREST + 24;
    const mats = treeMaterials();
    const geos = {
      pineTrunk: pineTrunk(), farTrunk: farTrunk(), leafyTrunk: leafyTrunk(), snag: snag(),
      pine: pineCrown(false), pineFar: pineCrown(true), leafy: leafyCrown(false), leafyFar: leafyCrown(true),
    };
    type Block = { farTrunk: Batch; pineTrunk: Batch; leafyTrunk: Batch; snag: Batch; pine: Batch; leafy: Batch; pineShadow: Batch; leafyShadow: Batch; near: boolean };
    const blocks = new Map<string, Block>();
    const blockAt = (x: number, y: number, near: boolean): Block => {
      const key = `${Math.floor(x / BLOCK)},${Math.floor(y / BLOCK)},${near}`;
      let b = blocks.get(key);
      if (!b) {
        b = {
          farTrunk: new Batch(geos.farTrunk, mats.bark, false), pineTrunk: new Batch(geos.pineTrunk, mats.bark, near), leafyTrunk: new Batch(geos.leafyTrunk, mats.bark, near), snag: new Batch(geos.snag, mats.bark, near),
          // the crowns near the base cast their shadows from stand-ins with the far trees' fewer
          // fronds and leaves: from overhead nobody can tell, and the shadow pass costs half
          pine: new Batch(near ? geos.pine : geos.pineFar, mats.needles, false, false, true),
          leafy: new Batch(near ? geos.leafy : geos.leafyFar, mats.leaves, false, false, true),
          pineShadow: new Batch(geos.pineFar, mats.needleShadow, true),
          leafyShadow: new Batch(geos.leafyFar, mats.leafShadow, true),
          near,
        };
        blocks.set(key, b);
      }
      return b;
    };
    // tints over the painted foliage: spruces from blue-green to olive, broadleaves from fresh to dry, a few turning
    const PINES = [0xd4e2cc, 0xbcd0c4, 0xe2e8c8, 0xc8d8bc, 0xb0c4b4];
    const LEAVES = [0xffffff, 0xe8f2d4, 0xf6efc4, 0xd4e4c8, 0xfff0b8, 0xf0d8a0];
    const SPACING = 1.75;
    const reach = FOREST_FAR;
    for (let gx = 0; gx < N + reach; gx += SPACING) {
      for (let gy = -reach; gy < N + reach; gy += SPACING) {
        const x = gx + r() * SPACING, y = gy + r() * SPACING;
        const d = out(x, y);
        if (d <= 0 || x < 2.5) continue;
        const edge = treeline(x, y);
        // thin at the edge, thick a few tiles in, with the odd clearing
        const clearing = smoothstep(0.55, 0.85, Math.sin(x * 0.09 + 2) * Math.sin(y * 0.11 - 1) + 0.25 * Math.sin(x * 0.3 + y * 0.27));
        if (r() > smoothstep(edge - 3, edge + 7, d) * 0.92 * (1 - clearing)) continue;
        if (onRoad(x, y, 0.9)) continue;
        const b = blockAt(x, y, d < NEAR);
        const rot = r() * Math.PI * 2;
        const lean: [number, number] = [(r() - 0.5) * 0.08, (r() - 0.5) * 0.08];
        // the trees nearest the fighting are burnt to bare, blackened trunks
        if (d < edge + 3 && r() < 0.35) {
          const h = 0.9 + r() * 1.1;
          b.snag.add({ x, y, rot, tilt: [(r() - 0.5) * 0.35, (r() - 0.5) * 0.35], s: [1.1, h, 1.1], color: 0x2a2520 });
          continue;
        }
        const size = 0.85 + r() * 0.55 + Math.min(0.35, (d - edge) * 0.01);
        const pine = r() < 0.55;
        const list = pine ? PINES : LEAVES;
        const s: [number, number, number] = [size, size * (0.9 + r() * 0.25), size];
        (b.near ? (pine ? b.pineTrunk : b.leafyTrunk) : b.farTrunk).add({ x, y, rot, tilt: lean, s, color: pine ? 0xa07a5c : 0xa09482 });
        const crown = { x, y, rot, tilt: lean, s, color: list[Math.floor(r() * list.length)]! };
        (pine ? b.pine : b.leafy).add(crown);
        if (b.near) (pine ? b.pineShadow : b.leafyShadow).add(crown);
      }
    }
    const crowns = new THREE.Group();
    g.add(crowns);
    for (const b of blocks.values()) {
      const leafy = [b.pine, b.leafy, b.pineShadow, b.leafyShadow];
      for (const batch of [b.farTrunk, b.pineTrunk, b.leafyTrunk, b.snag, ...leafy]) {
        if (!batch.puts.length) continue;
        const m = batch.build();
        m.computeBoundingSphere();
        m.frustumCulled = true;
        m.receiveShadow = b.near && batch !== b.pineShadow && batch !== b.leafyShadow;
        (leafy.includes(batch) ? crowns : g).add(m);
      }
    }
  }

  /** Drops a copy of a unit or building model out here, burnt black if it is a wreck. */
  private loadModel(name: string, x: number, y: number, rot: number, scale: number, burnt = false): void {
    const holder = new THREE.Group();
    holder.position.set(x - C, terrain(x, y), y - C);
    holder.rotation.y = rot;
    holder.scale.setScalar(scale);
    this.group.add(holder);
    Outskirts3D.model(name).then(src => {
      if (!src) return;
      const m = src.clone(true);
      m.traverse(o => {
        if (!(o instanceof THREE.Mesh)) return;
        o.castShadow = o.receiveShadow = true;
        if (burnt) {
          const mat = (o.material as THREE.MeshStandardMaterial).clone();
          if (mat.color) mat.color.multiplyScalar(0.18).lerp(new THREE.Color(0x2a1a10), 0.3);
          if ('roughness' in mat) mat.roughness = 1;
          if ('emissive' in mat) mat.emissive.set(0);
          o.material = mat;
        }
      });
      if (burnt) { m.rotation.z = 0.06; m.position.y = -0.03; }
      holder.add(m);
    }).catch(() => {});
  }

  private static readonly cache = new Map<string, Promise<THREE.Object3D | null>>();
  private static model(name: string): Promise<THREE.Object3D | null> {
    let p = Outskirts3D.cache.get(name);
    if (!p) {
      p = new GLTFLoader().loadAsync(`${import.meta.env.BASE_URL}models/${name}.glb`).then(g => g.scene, () => null);
      Outskirts3D.cache.set(name, p);
    }
    return p;
  }
}
